import { useCallback, useContext, useRef } from 'react'
import pointer from '../../utils/json-pointer'
import DataContext from '../../DataContext/Context'
import usePath from '../../hooks/usePath'
import type { Path } from '../../types'
import type { FormVisibilityProps } from './Visibility'

export type { FormVisibilityProps }

export default function useVisibility(
  props?: Partial<FormVisibilityProps>
) {
  const {
    hasFieldError,
    filterDataHandler,
    mountedFieldsRef,
    data: originalData,
  } = useContext(DataContext)

  const { makePath, makeIteratePath } = usePath()

  // Forward props to the "check" method with ref to avoid infinite loop
  const propsRef = useRef(props)
  propsRef.current = props

  const { withinIterate } = props || {}
  const makeLocalPath = useCallback(
    (path: Path) => {
      if (withinIterate) {
        return makeIteratePath(path)
      }

      return makePath(path)
    },
    [makeIteratePath, makePath, withinIterate]
  )

  const check = useCallback(
    (
      {
        visible,
        visibleWhen,
        visibleWhenNot,
        pathDefined,
        pathUndefined,
        pathTruthy,
        pathFalsy,
        pathTrue,
        pathFalse,
        inferData,
        filterData,
      }: Partial<FormVisibilityProps> = propsRef.current
    ) => {
      if (typeof visible === 'boolean') {
        return visible
      }

      const data =
        (filterData && filterDataHandler?.(originalData, filterData)) ||
        originalData

      if (visibleWhen || visibleWhenNot) {
        if (visibleWhenNot) {
          visibleWhen = visibleWhenNot
        }

        const path =
          'itemPath' in visibleWhen
            ? makeIteratePath(visibleWhen.itemPath)
            : makePath(visibleWhen.path)

        if ('isValid' in visibleWhen) {
          const item = mountedFieldsRef.current.get(path)
          if (!item || item.isMounted !== true) {
            return Boolean(visibleWhenNot)
          }
          const result =
            (visibleWhen.validateContinuously
              ? true
              : item.isFocused !== true) && !hasFieldError(path)
          return visibleWhenNot ? !result : result
        }

        if ('hasValue' in visibleWhen) {
          const hasPath = pointer.has(data, path)
          const value = hasPath ? pointer.get(data, path) : undefined

          const hasValue = visibleWhen?.['hasValue']
          const result =
            typeof hasValue === 'function'
              ? hasValue(value) === false
              : hasValue !== value

          if (visibleWhenNot) {
            if (!result) {
              return false
            }
          } else if (result) {
            return false
          }
        }
      }

      const getValue = (path: Path) => {
        if (pointer.has(data, path)) {
          return pointer.get(data, path)
        }
      }

      if (pathDefined) {
        return getValue(makeLocalPath(pathDefined)) !== undefined
      }
      if (pathUndefined) {
        return getValue(makeLocalPath(pathUndefined)) === undefined
      }

      if (pathTrue && getValue(makeLocalPath(pathTrue)) !== true) {
        return false
      }
      if (pathFalse && getValue(makeLocalPath(pathFalse)) !== false) {
        return false
      }

      if (pathTruthy && !getValue(makeLocalPath(pathTruthy))) {
        return false
      }
      if (pathFalsy && getValue(makeLocalPath(pathFalsy))) {
        return false
      }

      if (inferData && !inferData(data)) {
        return false
      }

      return true
    },
    [
      filterDataHandler,
      originalData,
      makeLocalPath,
      makeIteratePath,
      makePath,
      mountedFieldsRef,
      hasFieldError,
    ]
  )

  return { check }
}
